export default {
  title: 'Roles & Permissions',
  subtitle: 'The roles people hold in this organization and in its workspaces, and what each one allows',
  createRole: 'Create Role',
  createWorkspaceRole: 'Create Workspace Role',
  tabs: {
    organization: 'Organization roles',
    workspace: 'Workspace roles',
  },
  systemRole: 'Built-in',
  systemRoleDescription: 'Roles declared by the platform: their name and permissions cannot be changed',
  workspaceSystemRole: 'Built-in workspace roles',
  workspaceSystemRoleDescription: 'Admin, editor and viewer, declared by the platform: they cannot be changed',
  customRole: 'Custom',
  customRoleDescription: 'Roles your organization defined, with the permissions it chose',
  workspaceCustomRole: 'Custom workspace roles',
  workspaceCustomRoleDescription: 'Workspace roles your organization defined, with the permissions it chose',
  sovereign: 'Full control',
  sovereignHint:
    'This role holds every organization permission. The last member holding such a role cannot be moved off it.',
  memberCount: '{count} member | {count} members',
  permissionCount: '{count} permission | {count} permissions',
  noCustomRoles: 'No custom roles created yet',
  noCustomWorkspaceRoles: 'No custom workspace roles created yet',
  noWorkspaceSeat:
    'Workspace roles are read through a workspace you belong to, and you belong to no workspace in this organization.',
  dormant: 'Off here',
  dormantHint: 'the feature behind it is off in this organization, so nobody exercises it for now',
  builtInDescriptions: {
    org: {
      owner: 'Owns the organization: everything an administrator may do, plus handing it over and ending it',
      admin: 'Administers the organization: its members, its roles, its settings and its workspaces',
      member: 'Belongs to the organization: creates workspaces, sees the ones it may, and invites',
      staff:
        "Operates the platform: moderation and its audit trail, every organization's assets, the platform's own files and seats, and its figures",
    },
    workspace: {
      admin: 'Administers this workspace: its assets, its settings and its members',
      editor: 'Uploads, edits, shares and trashes assets, builds collections out of them, and manages their subtitles',
      viewer: 'Sees this workspace and everything in it, and changes nothing',
    },
  },
  create: {
    title: 'Create Custom Role',
    workspaceTitle: 'Create Custom Workspace Role',
    nameLabel: 'Role Name',
    namePlaceholder: 'e.g. Editor',
    keyLabel: 'Key',
    keyHint: 'Lowercase letters, digits and underscores. It cannot be changed afterwards.',
    keyInvalid: 'Use 1 to 64 lowercase letters, digits or underscores.',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe what this role can do...',
    success: 'Role created successfully',
  },
  detail: {
    backToRoles: 'Back to Roles',
    roleInfo: 'Role Information',
    permissions: 'Permissions',
    permissionsDescription: 'Select which permissions this role should have',
    builtinNote:
      'Built-in roles are declared by the platform: their name and permissions cannot be changed. Create a custom role instead.',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    dangerZone: 'Danger Zone',
    notFound: 'This role does not exist in this organization.',
    holders: 'Members with this role',
    holdersDescription: 'Who holds this role in the organization',
    noHolders: 'Nobody holds this role.',
    workspaceHoldersDescription: 'Who holds this role, and in which workspaces',
    holdersNeedMembersManage: 'Only a member allowed to manage members sees who holds this role.',
  },
  delete: {
    title: 'Delete Role',
    actionTitle: 'Delete this role',
    warning: 'This role will be permanently deleted. This action cannot be undone.',
    hasMembers: 'This role cannot be deleted because {count} member(s) hold it. Move them to another role first.',
    inUseHint: 'A role still held by a member, or offered by a pending invitation, cannot be deleted.',
    confirm: 'Delete Role',
    success: 'Role deleted successfully',
  },
  groups: {
    auth_members: 'Members',
    auth_org: 'Organization',
    auth_roles: 'Roles',
    auth_api_keys: 'API keys',
    analytics_app: 'Analytics',
    analytics_sessions: 'Playback sessions',
    subtitles_scribe: 'Scribe',
    tenancy_billing: 'Billing',
    tenancy_settings: 'Settings',
    tenancy_invitations: 'Invitations',
    tenancy_workspaces: 'Workspaces',
    assets_asset: 'Assets',
    assets_library: 'Library',
    assets_collection: 'Collections',
    assets_subtitle: 'Subtitles',
    assets_workspace: 'Workspace',
    assets_members: 'Workspace members',
    tenancy_membership_requests: 'Membership requests',
    websites: 'Channels',
    onisep_partners: 'ONISEP',
  },
  permissionNames: {
    auth_members_manage: 'Manage members',
    auth_members_invite: 'Invite people',
    auth_org_transfer: 'Transfer the organization',
    auth_org_delete: 'Delete the organization',
    auth_roles_manage: 'Manage roles',
    auth_api_keys_manage: 'Manage API keys',
    analytics_app_access: 'Open Analytics',
    analytics_sessions_read: 'Read the playback sessions through the API',
    subtitles_scribe_access: 'Open Scribe',
    tenancy_billing_manage: 'Manage billing',
    tenancy_settings_manage: 'Manage organization settings',
    tenancy_invitations_manage_all: 'Manage every invitation',
    tenancy_workspaces_create: 'Create workspaces',
    tenancy_workspaces_view_all: 'See every workspace',
    tenancy_workspaces_view_public: 'See public workspaces',
    tenancy_workspaces_access_all: 'Join every workspace',
    tenancy_workspaces_manage_all: 'Administer every workspace',
    assets_asset_create: 'Create and upload assets',
    assets_library_export: 'Export the whole library',
    assets_library_read: 'Read the library through the API',
    assets_library_upload: 'Add files to the library through the API',
    assets_library_edit: 'Edit the library through the API',
    assets_asset_delete: 'Permanently delete assets',
    assets_asset_edit: 'Edit asset details',
    assets_asset_export: 'Export asset data',
    assets_asset_moderate: 'Moderate assets',
    assets_asset_share: 'Share assets',
    assets_asset_trash: 'Move assets to the trash and restore them',
    assets_asset_view_analytics: "View an asset's analytics",
    assets_collection_create: 'Create collections',
    assets_collection_delete: 'Delete collections',
    assets_collection_edit: 'Edit collections',
    assets_subtitle_edit: 'Create, generate, import and edit subtitles',
    assets_subtitle_delete: 'Delete subtitles',
    assets_workspace_delete: 'Delete the workspace',
    assets_workspace_invite_members: 'Invite members to the workspace',
    assets_workspace_manage: 'Manage workspace settings',
    assets_workspace_manage_members: 'Manage workspace members',
    assets_members_invite: 'Invite members to the workspace',
    assets_members_manage: 'Manage workspace members',
    assets_third_party_video_import: 'Import videos from other platforms',
    tenancy_membership_requests_review: 'Review membership requests',
    websites_website_create: 'Create sites',
    websites_website_manage: "Manage a site's settings",
    websites_website_delete: 'Delete a site',
    websites_catalogue_edit: "Manage a site's catalogue",
    websites_catalogue_read: 'Read the channels through the API',
    websites_catalogue_publish: 'Publish on the channels through the API',
    websites_pages_edit: "Edit a site's pages",
    websites_members_view: "See a site's members",
    websites_members_manage: "Manage a site's members",
    websites_grants_manage: "Manage access to a site's content",
    websites_storefront_edit: "Manage a site's offers and subscriptions",
    websites_comments_moderate: "Moderate a site's comments",
    websites_submissions_manage: "Handle a site's requests and messages",
    websites_stats_view: "See a site's statistics",
    onisep_partners_manage: 'Manage ONISEP partners',
  },
  // The server's own descriptions are English already, so this catalogue names none and the
  // role editor shows them as they are. See the French catalogue.
  permissionDescriptions: {},
  // The short form of the same keys — see the French catalogue.
  permissionPhrases: {
    verbs: {
      assets_asset_create: 'upload',
      assets_asset_edit: 'edit',
      assets_asset_share: 'share',
      assets_asset_trash: 'trash',
      assets_asset_delete: 'permanently delete',
      assets_asset_export: 'export the data of',
      assets_asset_moderate: 'moderate',
      assets_asset_view_analytics: 'view the analytics of',
      assets_collection_create: 'create',
      assets_collection_edit: 'edit',
      assets_collection_delete: 'delete',
      assets_subtitle_edit: 'create and edit',
      assets_subtitle_delete: 'delete',
      assets_members_invite: 'invite',
      assets_members_manage: 'manage',
      assets_workspace_manage: 'manage the workspace settings',
      assets_workspace_delete: 'delete the workspace',
    },
    objects: {
      assets_asset: 'assets',
      assets_collection: 'collections',
      assets_subtitle: 'subtitles',
      assets_members: 'members',
    },
  },
  errors: {
    loadFailed: 'Failed to load roles',
    saveFailed: 'Failed to save changes',
    createFailed: 'Failed to create role',
    deleteFailed: 'Failed to delete role',
    keyTaken: 'A role with this key already exists in this organization.',
    invalid: 'This role cannot be saved: check its key and its name. Built-in roles cannot be changed.',
    aboveOwnRole: 'You cannot give a role a permission your own role does not hold.',
    forbidden: "You may not change this organization's workspace roles.",
    inUse: 'This role is still held by a member or offered by a pending invitation. Move them to another role first.',
    seatsHoldIt: 'This role is still held in a workspace. Move those members to another role first.',
    lastAdministrator: 'This change would leave nobody able to administer the organization.',
    notFound: 'This role no longer exists.',
    network: 'The server could not be reached. Try again.',
  },
}
